import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import { useFormikContext } from "formik";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import dayjs from "dayjs";
import { CustomFieldLabel } from "../../components/fields/CustomFieldLabel/CustomFieldLabel";
import { OrderProjectValues } from "./helper";
import {
  PRIMARY_COLOR,
  getMilestones,
  addDays,
  formatDate,
  MilestoneStepper,
} from "../../components/ProjectTimeline";

function LocalMilestoneStepper({
  startDate,
  numProjects,
}: {
  startDate: Date;
  numProjects: number;
}) {
  const milestones = getMilestones(numProjects).map((m) => ({
    label: m.label,
    text: formatDate(addDays(startDate, m.daysOffset)),
  }));
  return <MilestoneStepper milestones={milestones} />;
}

interface TimelineEstimatorProps {
  numProjects?: number;
}

export const TimelineEstimator: React.FC<TimelineEstimatorProps> = ({
  numProjects = 0,
}) => {
  const { values, setFieldValue } = useFormikContext<{
    start_date: Date | null;
    projects: OrderProjectValues[];
  }>();

  const startDate = values.start_date ?? new Date();

  const [showTimeline, setShowTimeline] = useState(false);

  const header = (
    <Box
      sx={{
        backgroundColor: PRIMARY_COLOR,
        color: "#fff",
        py: 2,
        px: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: { xs: "space-between", md: "center" },
        gap: 1,
        borderRadius: "1rem 1rem 0 0",
        flexShrink: 0,
      }}
    >
      <Typography
        sx={{
          fontFamily: "CircularStd, sans-serif",
          fontWeight: 700,
          fontSize: 15,
          color: "#fff",
        }}
      >
        Timeline Estimator
      </Typography>
      <Typography
        component="button"
        type="button"
        onClick={() => setShowTimeline(true)}
        sx={{
          display: { xs: "inline-flex", md: "none" },
          alignItems: "center",
          background: "none",
          border: "none",
          p: 0,
          cursor: "pointer",
          color: "#fff",
          fontFamily: "CircularStd, sans-serif",
          fontWeight: 600,
          fontSize: 13,
          textDecoration: "underline",
        }}
      >
        View
      </Typography>
    </Box>
  );

  const projects = values.projects;

  const firstProjectKey = projects[0]?.id ?? (projects.length > 0 ? "0" : null);
  const [expandedId, setExpandedId] = useState<string | null>(firstProjectKey);

  useEffect(() => {
    setExpandedId((prev) => {
      const ids = projects.map((p, i) => p.id ?? String(i));
      if (prev && ids.includes(prev)) return prev;
      return ids[0] ?? null;
    });
  }, [projects]);

  const projectStartDates = projects.map((p) =>
    p.start_date instanceof Date ? p.start_date : new Date(),
  );

  const deliveryDaysOffset = getMilestones(1).at(-1)!.daysOffset;

  const earliestStart =
    projectStartDates.length > 0
      ? new Date(Math.min(...projectStartDates.map((d) => d.getTime())))
      : new Date();

  const latestDelivery =
    projectStartDates.length > 0
      ? new Date(
          Math.max(
            ...projectStartDates.map((d) =>
              addDays(d, deliveryDaysOffset).getTime(),
            ),
          ),
        )
      : addDays(new Date(), deliveryDaysOffset);

  const emptyTimeline = (
    <Box
      sx={{
        minHeight: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 2,
      }}
    >
      <Typography
        sx={{
          fontFamily: "CircularStd, sans-serif",
          fontSize: 13,
          color: "var(--text-2)",
          textAlign: "center",
        }}
      >
        Add projects to see individual timelines
      </Typography>
    </Box>
  );

  const timelineList =
    projects.length === 0
      ? emptyTimeline
      : projects.map((project, idx) => {
          const projStart =
            project.start_date instanceof Date ? project.start_date : new Date();
          return (
            <Accordion
              key={project.id ?? idx}
              expanded={expandedId === (project.id ?? String(idx))}
              onChange={(_, isExpanded) =>
                setExpandedId(isExpanded ? project.id ?? String(idx) : null)
              }
              disableGutters
              sx={{
                borderRadius: "none",
                "&:before": { display: "none" },
                boxShadow: "none",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: PRIMARY_COLOR }} />}
                sx={{
                  gap: 1.5,
                  alignItems: "center",
                  "& .MuiAccordionSummary-content": { mr: 0, alignItems: "center" },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "CircularStd, sans-serif",
                    fontWeight: 800,
                    fontSize: 14,
                    textTransform: "capitalize",
                    color: PRIMARY_COLOR,
                  }}
                >
                  {project.product_name || `Project ${idx + 1}`}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
                <LocalMilestoneStepper startDate={projStart} numProjects={1} />
              </AccordionDetails>
            </Accordion>
          );
        });

  return (
    <>
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        margin: "0 1rem",
        overflow: "hidden",
        border: "1px solid",
        borderColor: PRIMARY_COLOR,
        borderRadius: "1rem",
      }}
    >
      {header}

      {/* Overall summary */}
      <Box sx={{ px: 2, py: 1.5, background: "var(--surface-2)", flexShrink: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
          <Box>
            <Typography
              sx={{
                fontFamily: "CircularStd, sans-serif",
                fontSize: 10,
                fontWeight: 700,
                color: "var(--text-2)",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Earliest Start
            </Typography>
            <Typography
              sx={{
                fontFamily: "CircularStd, sans-serif",
                fontSize: 13,
                fontWeight: 600,
                color: "var(--text)",
              }}
            >
              {formatDate(earliestStart)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{
                fontFamily: "CircularStd, sans-serif",
                fontSize: 10,
                fontWeight: 700,
                color: "var(--text-2)",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              Latest Delivery
            </Typography>
            <Typography
              sx={{
                fontFamily: "CircularStd, sans-serif",
                fontSize: 13,
                fontWeight: 600,
                color: "var(--text)",
              }}
            >
              {formatDate(latestDelivery)}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          display: { xs: "none", md: "block" },
        }}
      >
        {timelineList}
      </Box>
    </Box>

    <Dialog
      open={showTimeline}
      onClose={() => setShowTimeline(false)}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontFamily: "CircularStd, sans-serif",
          fontWeight: 700,
          fontSize: 16,
          pb: 1,
        }}
      >
        Project Timelines
        <IconButton onClick={() => setShowTimeline(false)} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>{timelineList}</DialogContent>
    </Dialog>
    </>
  );
};
